// Save and propagate: finds the sibling locations linked through Shared Settings
// and resolves which source attribute feeds each target attribute.

import { useCallback, useState } from 'react';

import {
  getHeaderValue,
  parseAttributeKey,
  setHeaderValue,
} from '../../features/dynamic-form';
import ConfiguredIntegrationsApi from '../../api/configured_integrations';
import IntegrationsApi from '../../api/integrations';

// Resolves one target attribute against the bindings the user confirmed in the
// modal. Two shapes are supported: an exact attribute name, and a dot-notated
// header sub-key ('headers1.KarmakAccountNumber') that patches a single entry.
const applyBindings = (attr, targetBindings, sourceAttributes) => {
  const exactBinding = targetBindings[attr.name];

  if (exactBinding?.bound && exactBinding.sourceAttrName) {
    const { baseName: srcBase, subKey: srcSubKey } = parseAttributeKey(
      exactBinding.sourceAttrName,
    );
    const sourceAttr = sourceAttributes.find((a) => a.name === srcBase);

    if (sourceAttr) {
      const sourceValue =
        srcSubKey !== null && Array.isArray(sourceAttr.value)
          ? getHeaderValue(sourceAttr.value, srcSubKey)
          : sourceAttr.value;
      return { ...attr, value: sourceValue };
    }
  }

  const headerBindings = Object.entries(targetBindings).filter(([key, b]) => {
    const { baseName, subKey } = parseAttributeKey(key);
    return baseName === attr.name && subKey !== null && b?.bound;
  });

  if (headerBindings.length === 0) return attr;

  let updatedHeaders = Array.isArray(attr.value) ? [...attr.value] : [];

  headerBindings.forEach(([dotKey, b]) => {
    if (!b.sourceAttrName) return;

    const { subKey: targetSubKey } = parseAttributeKey(dotKey);
    const { baseName: srcBase, subKey: srcSubKey } = parseAttributeKey(
      b.sourceAttrName,
    );
    const sourceAttr = sourceAttributes.find((a) => a.name === srcBase);

    if (!sourceAttr || targetSubKey === null) return;

    const sourceValue =
      srcSubKey !== null && Array.isArray(sourceAttr.value)
        ? getHeaderValue(sourceAttr.value, srcSubKey)
        : sourceAttr.value;

    if (sourceValue !== undefined) {
      updatedHeaders = setHeaderValue(
        updatedHeaders,
        targetSubKey,
        sourceValue,
      );
    }
  });

  return { ...attr, value: updatedHeaders };
};

export default function usePropagateSettings({
  configuredIntegration,
  integration,
  allSharedSettings,
  onFetchFailed,
}) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [targets, setTargets] = useState([]);
  const [integrationNames, setIntegrationNames] = useState({});
  const [sourceIntegrationName, setSourceIntegrationName] = useState('');
  const [pendingAttributes, setPendingAttributes] = useState(null);

  const close = useCallback(() => {
    setVisible(false);
    setLoading(false);
    setSourceIntegrationName('');
  }, []);

  const open = useCallback(
    async (attributes) => {
      setLoading(true);
      setSourceIntegrationName(integration.attributes.name || '');
      setVisible(true);
      setPendingAttributes(attributes);

      const { data: allConfiguredIntegrations, errors: fetchErrors } =
        await ConfiguredIntegrationsApi.fetchConfiguredIntegrationsBySrmAccountId(
          configuredIntegration.attributes.srm_account_id,
        );

      if (fetchErrors) {
        onFetchFailed();
        setVisible(false);
        setLoading(false);
        return false;
      }

      const candidates = (allConfiguredIntegrations || []).filter(
        (ci) =>
          ci.id !== configuredIntegration.id &&
          (ci.attributes.location_settings_attributes || []).length > 0,
      );

      const uniqueIntegrationUuids = [
        ...new Set(candidates.map((ci) => ci.attributes.integration_uuid)),
      ];

      // fetchIntegrationByUuid also loads step functions, which are not needed
      // here and make the payload much larger.
      const integrationFetches = await Promise.all(
        uniqueIntegrationUuids.map((uuid) =>
          IntegrationsApi.fetchIntegrationBasicInfo(uuid),
        ),
      );

      const namesMap = {};
      integrationFetches.forEach((result, index) => {
        if (result.data) {
          namesMap[uniqueIntegrationUuids[index]] = result.data.attributes.name;
        }
      });

      const sourceUuid = integration.id;

      setTargets(
        candidates.filter((ci) =>
          allSharedSettings.some(
            (s) =>
              (s.integration_uuids || []).includes(sourceUuid) &&
              (s.integration_uuids || []).includes(
                ci.attributes.integration_uuid,
              ),
          ),
        ),
      );
      setIntegrationNames(namesMap);
      setLoading(false);
      return true;
    },
    [configuredIntegration, integration, allSharedSettings, onFetchFailed],
  );

  const propagateTo = useCallback(
    (bindingMap) =>
      Promise.all(
        targets.map((target) => {
          const targetBindings = bindingMap[target.id] || {};
          const updatedAttrs = (
            target.attributes.location_settings_attributes || []
          ).map((attr) =>
            applyBindings(attr, targetBindings, pendingAttributes),
          );

          return ConfiguredIntegrationsApi.updateConfiguredIntegration(
            target.id,
            { location_settings_attributes: updatedAttrs },
          );
        }),
      ),
    [targets, pendingAttributes],
  );

  return {
    visible,
    loading,
    saving,
    targets,
    integrationNames,
    sourceIntegrationName,
    pendingAttributes,
    setSaving,
    setVisible,
    open,
    close,
    propagateTo,
  };
}
